/**
 * API Error Response Interface
 * Based on RFC 7807 - Problem Details for HTTP APIs
 * https://tools.ietf.org/html/rfc7807
 */
export interface ApiErrorResponse {
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  type?: string;
  [key: string]: unknown;
}

/**
 * Custom API Error Class
 * Extends the native Error class to include structured API error information
 */
export class ApiError extends Error {
  public readonly status: number;
  public readonly title: string;
  public readonly detail?: string;
  public readonly instance?: string;
  public readonly type?: string;
  public readonly errorResponse?: ApiErrorResponse;

  constructor(errorResponse: Partial<ApiErrorResponse>, fallbackMessage?: string) {
    const message = ApiError.formatErrorMessage(errorResponse, fallbackMessage);
    super(message);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }

    this.name = 'ApiError';
    this.status = errorResponse.status ?? 0;
    this.title = errorResponse.title ?? 'Unknown Error';
    this.detail = errorResponse.detail;
    this.instance = errorResponse.instance;
    this.type = errorResponse.type;
    this.errorResponse = errorResponse as ApiErrorResponse;

    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /**
   * Formats the error message from the error response
   * @param errorResponse - The API error response
   * @param fallbackMessage - Optional fallback message if response is incomplete
   * @returns Formatted error message
   */
  private static formatErrorMessage(
    errorResponse: Partial<ApiErrorResponse>,
    fallbackMessage?: string
  ): string {
    const { title, detail } = errorResponse;

    if (title && detail && title !== detail) {
      return `${title}: ${detail}`;
    }

    return title || detail || fallbackMessage || 'An unexpected error occurred';
  }

  public getDetailedMessage(): string {
    const parts = [
      this.status && `[${this.status}]`,
      this.title,
      this.detail && this.detail !== this.title && `- ${this.detail}`,
      this.instance && `(${this.instance})`
    ].filter(Boolean);

    return parts.join(' ') || this.message;
  }
}

export function isApiErrorResponse(obj: unknown): obj is ApiErrorResponse {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }

  const response = obj as Record<string, unknown>;
  return (
    typeof response.title === 'string' &&
    typeof response.status === 'number'
  );
}

